// File: frontend/src/hooks/useDfsResearchQueries.ts
// Shared TanStack query definitions for the builder's Stats (ResearchTab) and
// Projections (ProjectionsTab) data. One definition per dataset, used by BOTH
// the tab (useQuery) and the page-level prefetch (queryClient.prefetchQuery),
// so the first tab click finds warm data and revisits render from cache.
//
// Both endpoints are public; the token is passed through when present but is
// deliberately NOT part of the query key (same data for all viewers).
import { queryOptions } from '@tanstack/react-query';
import { fetchPlayerProjections } from '@/api/dfs';
import { fetchPlayersStatsForCompetitionActiveSeason } from '@/api/stats';

// Matches useDfsPlayerPool: pool-adjacent data changes on task cadence
// (salaries per contest, projections on the beat, lineups ~1h pre-kickoff).
const RESEARCH_STALE_TIME = 1000 * 60 * 10; // 10 minutes
const RESEARCH_GC_TIME = 1000 * 60 * 15;    // 15 minutes

export const statsLeaderboardQueryOptions = (
    competitionId: number | null | undefined,
    token?: string | null,
) =>
    queryOptions({
        queryKey: ['statsLeaderboard', competitionId ?? 0],
        queryFn: () =>
            fetchPlayersStatsForCompetitionActiveSeason(
                competitionId!,
                { limit: 1500, sort_by: 'goals', order: 'desc' },
                token,
            ),
        enabled: !!competitionId,
        staleTime: RESEARCH_STALE_TIME,
        gcTime: RESEARCH_GC_TIME,
    });

export const contestProjectionsQueryOptions = (
    contestId: number | null | undefined,
    token?: string | null,
) =>
    queryOptions({
        queryKey: ['contestPlayerProjections', contestId ?? 0],
        queryFn: () => fetchPlayerProjections(contestId!, token ?? null),
        enabled: !!contestId,
        staleTime: RESEARCH_STALE_TIME,
        gcTime: RESEARCH_GC_TIME,
    });
